// external imports
import { useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/system';
import { Box, Pagination } from '@mui/material';
import axios from 'axios';
import {
  Filter,
  NoResults,
  ReaderCard,
  Spinner,
} from '../../components/index';
import {
  buildReadersObject,
  theme,
} from '../../lib/index';

// variables
const {
  REACT_APP_DATABASE_USER,
  REACT_APP_DATABASE_PW,
  REACT_APP_DATABASE_ROOT_URI,
} = process.env;

export const initialFilter = {
  name: null,
  focus: [],
  location: null,
  languages: [],
};

// shuffle reader-cards
/* eslint-disable */
function shuffle(array) {
  let m = array.length; let t; let
    i;
  while (m) {
    i = Math.floor(Math.random() * m--);
    t = array[m];
    array[m] = array[i];
    array[i] = t;
  }
  return array;
}
/* eslint-enable */

// create component
function Layout({ screenWidth }) {
  // pagination settings
  const numberOfItemsOnOnePage = screenWidth && screenWidth < 1024 ? 10 : 6;

  const [loading, setLoading] = useState(true);
  const [readerData, setReaderData] = useState([]);
  const [readersForFilter, setReadersForFilter] = useState({});
  const [filteredReaderData, setFilteredReaderData] = useState(readerData);
  const [
    slicedReaderData,
    setSlicedReaderData,
  ] = useState(filteredReaderData.slice(0, numberOfItemsOnOnePage));
  const [filter, setFilter] = useState(initialFilter);

  // fetch data
  const fetchData = useCallback(async () => {
    const { data } = await axios.get(
      REACT_APP_DATABASE_ROOT_URI,
      {
        params: { per_page: 999 },
        auth: {
          username: REACT_APP_DATABASE_USER,
          password: REACT_APP_DATABASE_PW,
        },
      }
    );
    const noInactiveProfiles = data.filter((e) => !e.acf.reader_calendar_inactive);
    const notPaused = shuffle(noInactiveProfiles.filter((e) => !e.acf.reader_calendar_pause));
    setReaderData(notPaused);
    setFilteredReaderData(notPaused);
    setLoading(false);
    setReadersForFilter(buildReadersObject(notPaused));
    setSlicedReaderData(notPaused.slice(0, numberOfItemsOnOnePage));
  }, [numberOfItemsOnOnePage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // filter
  useEffect(() => {
    // @TODO: Switch from imperative to declarative style
    const ids = [];
    let filterCounter = 0;
    const idCounter = {};
    // filter readers for values in filter-object
    Object.entries(readersForFilter).map(
      (item) => {
        const obj = item[1];
        /* eslint-disable no-restricted-syntax,guard-for-in */
        for (const key in filter) {
          const filterItem = filter[key];
          const objItem = obj[key];
          // multiselect inputs return array
          if (key === 'focus' || key === 'languages') {
            if (
              filterItem.length > 0
              && filterItem.every((innerItem) => objItem.indexOf(innerItem) >= 0)
            ) {
              ids.push(obj.id);
            }
          // single inputs return string
          } else if (filterItem !== null && objItem.includes(filterItem)) {
            ids.push(obj.id);
          }
        }
        /* eslint-enable no-restricted-syntax,guard-for-in */
        return ids;
      }
    );
    // count how many filters are used
    Object.entries(filter).forEach((key) => {
      if (key[1] !== null && key[1].length !== 0) {
        // eslint-disable-next-line
        filterCounter++;
      }
    });
    // check for ids that have been filtered filterCounter-times
    ids.forEach((x) => { idCounter[x] = (idCounter[x] || 0) + 1; });
    const filteredIds = ids.filter((id) => idCounter[id] === filterCounter);
    // render readers with filtered ids
    if (filterCounter === 0) {
      setFilteredReaderData(readerData);
    } else {
      setFilteredReaderData(
        readerData.filter((reader) => filteredIds.includes(reader.id))
      );
    }
  }, [filter, readerData, readersForFilter]);

  // slice for pagination
  useEffect(() => {
    if (filteredReaderData.length <= numberOfItemsOnOnePage) {
      setSlicedReaderData(filteredReaderData);
    } else {
      setSlicedReaderData(
        filteredReaderData.slice(0, numberOfItemsOnOnePage)
      );
    }
  }, [filteredReaderData, numberOfItemsOnOnePage]);

  // reset Filter
  const resetFilter = () => {
    setFilter(initialFilter);
  };

  // use pagination on cards
  const handlePagination = (page) => {
    const first = (page - 1) * numberOfItemsOnOnePage;
    const last = page * numberOfItemsOnOnePage;
    const sliceReaders = filteredReaderData.slice(first, last);
    setSlicedReaderData(sliceReaders);
    window.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      <InnerContainer>
        <Filter
          data={readerData}
          filter={filter}
          setFilter={setFilter}
          resetFilter={resetFilter}
          initialFilter={initialFilter}
          screenWidth={screenWidth}
        />
        { loading ? (
          <Spinner />
        ) : (
          <CardContainer>
            {
                slicedReaderData.length !== 0
                  ? slicedReaderData.map((e) => <ReaderCard key={e.id} data={e} />)
                  : <NoResults resetFilter={resetFilter} />
              }
            {
                filteredReaderData.length > numberOfItemsOnOnePage && (
                  <StyledPagination
                    count={
                      filteredReaderData.length > 0
                        ? Math.ceil(
                          filteredReaderData.length / numberOfItemsOnOnePage
                        )
                        : 1
                    }
                    onChange={(e, value) => handlePagination(value)}
                    shape="rounded"
                    variant="outlined"
                  />
                )
              }
          </CardContainer>
        )}
      </InnerContainer>
    </Wrapper>
  );
}

export default Layout;

// styling
const Wrapper = styled(Box, {})({
  boxSizing: 'border-box',

  width: '100%',

  background: theme.palette.backgroundgrey,
});

const InnerContainer = styled(Box, {})({
  margin: '0 auto',
  padding: 0,
  height: '100%',
  maxWidth: 1200,

  [theme.breakpoints.up('laptop')]: {
    padding: '60px 0',
    minHeight: 'unset',
    maxHeight: 'unset',
  },
});

const CardContainer = styled(Box, {})({
  display: 'flex',
  paddingLeft: 0,

  gap: 24,
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'top',
});

const StyledPagination = styled(Pagination, {})({
  position: 'fixed',
  bottom: 0,

  display: 'flex',
  padding: '20px 0',
  width: '100%',

  justifyContent: 'center',

  borderRadius: '5px 5px 0 0',
  background: theme.palette.white,
  boxShadow: `0px 12px 2px 13px ${theme.palette.placeholdergrey}`,

  '& .MuiPaginationItem-root': {
    background: theme.palette.white,

    '&.Mui-selected': {
      background: theme.palette.newSecondary,
    },

    '&:hover': {
      background: theme.palette.midgrey,
    },
  },

  [theme.breakpoints.up('laptop')]: {
    position: 'relative',

    borderRadius: 0,
    background: 'none',
    boxShadow: 'none',
  },
});
